import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import type { ProductDto } from '@cardhub/protocol';
import { api } from '../api/client';
import { PayPalCheckout, usePayPalClientId } from '../components/PayPalCheckout';
import { RewardedAdPlayer, useRewardedAd } from '../components/RewardedAd';
import { Coins, Empty, ErrorState, Loading, TopBar } from '../components/ui';
import { useT } from '../i18n';
import { errorMessage, useAsync } from '../lib/hooks';
import { isNative } from '../native/platform';
import { buyNative, loadLocalizedPrices, type StoreProduct } from '../native/purchases';
import { useSession } from '../store/session';
import { toast } from '../store/ui';

export function Store() {
  const t = useT();
  const nav = useNavigate();
  const balance = useSession((s) => s.balance);
  const user = useSession((s) => s.user)!;
  const products = useAsync(() => api.get<{ products: ProductDto[]; enabled: boolean }>('/api/v1/store/products'), []);
  const [items, setItems] = useState<StoreProduct[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const paypalId = usePayPalClientId();
  const [checkout, setCheckout] = useState<StoreProduct | null>(null);
  const ad = useRewardedAd('lobby_free_coins');

  useEffect(() => {
    if (products.data) void loadLocalizedPrices(products.data.products).then(setItems);
  }, [products.data]);

  const canBuy = isNative || Boolean(paypalId);

  const buy = async (p: StoreProduct) => {
    if (!isNative) {
      setCheckout(p);
      return;
    }
    setBusy(p.id);
    try {
      const r = await buyNative(p, user.id);
      toast(`+${new Intl.NumberFormat().format(r.coins)} coins`, 'success');
    } catch (e) {
      const msg = errorMessage(e);
      // User cancelling the store sheet is not an error worth shouting about.
      if (!/cancel/i.test(String((e as Error)?.message ?? ''))) toast(msg, 'error');
    } finally {
      setBusy(null);
    }
  };

  const price = (p: StoreProduct) => p.localizedPrice ?? new Intl.NumberFormat(undefined, { style: 'currency', currency: p.currency }).format(p.priceMicros / 1e6);

  return (
    <main className="page">
      <TopBar title={t('store.title')} onBack={() => nav(-1)} right={<Coins amount={balance} />} />
      <div className="card spread" style={{ marginBottom: 16, background: 'linear-gradient(135deg, rgba(124,92,255,0.25), rgba(34,211,166,0.18))' }}>
        <span>
          <strong>📺 {t('store.freeCoins')}</strong>
          <br />
          <span className="dim">{t('ads.reward', { n: 500 })}</span>
        </span>
        <button className="btn primary" onClick={() => void ad.start()} disabled={ad.busy}>
          {t('ads.watch')}
        </button>
      </div>
      {products.loading && <Loading />}
      {products.error && <ErrorState message={products.error} onRetry={products.reload} />}
      {products.data && !products.data.enabled && <Empty emoji="🛒" title={t('store.disabled')} />}
      {products.data?.enabled && !canBuy && <Empty emoji="✨" title="Purchases are coming soon" body="We're putting the finishing touches on secure checkout. Check back shortly. In the meantime, you can earn free coins from daily rewards, missions and videos." />}
      {products.data?.enabled && canBuy && (
        <div className="games-grid">
          {items.map((p) => (
            <div key={p.id} className="card col" style={{ alignItems: 'center', textAlign: 'center', position: 'relative' }}>
              {p.badge && <span className="pill gold" style={{ position: 'absolute', top: 10, right: 10 }}>{p.badge}</span>}
              <div style={{ fontSize: 42 }} aria-hidden>
                {p.coins >= 500000 ? '💰' : p.coins >= 100000 ? '🪙' : '🟡'}
              </div>
              <Coins amount={p.coins} size={22} />
              {p.bonusCoins > 0 && <span className="pill accent">+{new Intl.NumberFormat().format(p.bonusCoins)} bonus</span>}
              <button className="btn gold block" disabled={busy !== null} onClick={() => void buy(p)}>
                {busy === p.id ? t('common.loading') : price(p)}
              </button>
            </div>
          ))}
        </div>
      )}
      <p className="muted" style={{ fontSize: 'var(--fs-xs)', textAlign: 'center', marginTop: 20 }}>{t('store.note')}</p>
      {checkout && (
        <PayPalCheckout
          open
          title={`${new Intl.NumberFormat().format(checkout.coins + checkout.bonusCoins)} coins`}
          priceLabel={price(checkout)}
          productId={checkout.id}
          onClose={() => setCheckout(null)}
          onPaid={() => {
            toast(`+${new Intl.NumberFormat().format(checkout.coins + checkout.bonusCoins)} coins`, 'success');
            setCheckout(null);
          }}
        />
      )}
      {ad.session && <RewardedAdPlayer session={ad.session} onClose={() => ad.close()} />}
    </main>
  );
}
